import { useEffect } from 'react';
import { Platform } from 'react-native';
import { useVideoPlayer, type VideoPlayer } from 'expo-video';
import { reaction } from 'mobx';

import { CardPhases } from '@/stores/workoutPlayer/types';
import type { IWorkoutPlayerStore } from '@/stores/workoutPlayer/WorkoutPlayerStore';
import { subscribeAutoPlay } from './playerUtils';

/**
 * Controller hook for card-based workouts.
 *
 * Owns the expo-video player instance and all playback wiring:
 * - Loads/replaces video source when store.currentVideoUrl changes
 * - Restarts video on phase transitions (DRAW_OUTRO→PLAY_INTRO, CHANGE_ACTIVE→CHANGE_OUTRO)
 * - Auto-plays when player becomes ready
 * - Forwards playToEnd events to the store
 * - Syncs play/pause with store.isPaused
 */
export function useCardPlayerController(store: IWorkoutPlayerStore): {
  player: VideoPlayer;
} {
  const player = useVideoPlayer(null, (p) => {
    p.loop = false; // Disabled so playToEnd fires on every loop (needed for rep counting)
    p.muted = true;
  });

  // Source loading: replace video when URL changes
  useEffect(() => {
    const dispose = reaction(
      () => store.currentVideoUrl,
      (url) => {
        player.replaceAsync(url).catch((err) => {
          console.warn('[useCardPlayerController] replaceAsync failed', err);
        });
      },
      { fireImmediately: true },
    );
    return dispose;
  }, [player, store]);

  // Phase-based video restart (replaces videoReplaySignal)
  useEffect(() => {
    const dispose = reaction(
      () => store.cardPhase,
      (newPhase, oldPhase) => {
        const needsRestart =
          (newPhase === CardPhases.PLAY_INTRO &&
            oldPhase === CardPhases.DRAW_OUTRO) ||
          (newPhase === CardPhases.CHANGE_OUTRO &&
            oldPhase === CardPhases.CHANGE_ACTIVE);

        if (needsRestart) {
          player.currentTime = 0;
          if (!store.isPaused) player.play();
        }

        // Ensure video plays during preview phase
        if (newPhase === CardPhases.DRAW_ACTIVE && !store.isPaused) {
          // From testing, web required some delay to reliably play after source replace
          if (Platform.OS === 'web') {
            setTimeout(() => {
              if (!store.isPaused && player.status === 'readyToPlay') {
                player.play();
              }
            }, 1000);
          } else if (player.status === 'readyToPlay') {
            player.play();
          }
        }
      },
    );
    return dispose;
  }, [player, store]);

  // Auto-play when player becomes ready
  useEffect(() => {
    const sub = subscribeAutoPlay(player, () => store.isPaused);
    return () => sub.remove();
  }, [player, store]);

  // Forward playToEnd to store, then restart video (manual loop since loop=false)
  useEffect(() => {
    const sub = player.addListener('playToEnd', () => {
      store.onPlayerEnd();
      player.currentTime = 0;
      if (!store.isPaused) player.play();
    });
    return () => sub.remove();
  }, [player, store]);

  // Pause/play sync
  useEffect(() => {
    const dispose = reaction(
      () => store.isPaused,
      (isPaused) => {
        if (isPaused) {
          player.pause();
        } else {
          // Always unmute on unpause; play() is gated on readiness because
          // subscribeAutoPlay will kick off playback once the source loads.
          player.muted = false;
          if (player.status === 'readyToPlay') {
            player.play();
          }
        }
      },
    );
    return dispose;
  }, [player, store]);

  return { player };
}
